'use client';
import styled, { css, keyframes } from 'styled-components';

export const CardWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin-top: ${props => props.$offsetBadge || 0}px;
`;

export const Card = styled.div`
  border-radius: ${({ $borderRadius }) =>
    $borderRadius ? `${$borderRadius}px` : '24px'};
  border: 1px solid
    ${props =>
      props.checked
        ? props.theme.colors.text.link
        : props.theme.colors.border.light};
  display: inline-flex;
  flex-direction: column;
  user-select: none;
  overflow: hidden; /* prevent overflow on rounded corners */
  position: relative; /* allow absolute positioning of child elements */
  width: ${({ $mobileWidth, width }) =>
    $mobileWidth ? `${$mobileWidth}px` : `${width}px`};
  max-height: ${props =>
    props.$mobileOrientation === 'column' ? '178px' : '110px'};
  min-height: ${props =>
    props.$mobileOrientation === 'column' ? '165px' : 'unset'};
  background: ${({ theme, $unavailable, $notSelectable, $bgColor, checked }) =>
    $unavailable || $notSelectable
      ? theme.colors.background.lightest
      : checked
      ? theme.colors.background.checked
      : theme.colors.background[$bgColor]};
  color: ${({ theme }) => theme.colors.text.primary};
  padding: ${props =>
    props.$mobileOrientation === 'column' && props.$offsetBadge
      ? props.theme.spacing(5, 0, 2)
      : props.$mobileOrientation === 'column'
      ? props.theme.spacing(2)
      : props.$offsetBadge
      ? props.theme.spacing(4, 0)
      : '0'};

  @media ${props => props.theme.laptop} {
    max-height: ${props =>
      props.$orientation === 'column' ? '178px' : '110px'};
    min-height: ${props =>
      props.$orientation === 'column' ? '165px' : 'unset'};
    padding: ${props =>
      props.$orientation === 'column' && props.$offsetBadge
        ? props.theme.spacing(5, 0, 2)
        : props.$orientation === 'column'
        ? props.theme.spacing(2)
        : props.$offsetBadge
        ? props.theme.spacing(4, 0)
        : '0'};
    width: ${props => `${props.width}px`};
  }
`;

export const CardContent = styled.div`
  display: flex;
  align-items: ${({ $alignColumnContent }) =>
    $alignColumnContent === 'left' ? 'flex-start' : 'center'};
  flex-direction: ${props => props.$mobileOrientation};
  flex: 1;
  gap: 1px;
  padding: ${props =>
    props.$mobileOrientation === 'column' &&
    props.$alignColumnContent === 'center'
      ? props.theme.spacing(2, 0)
      : props.theme.spacing(2)};

  @media ${props => props.theme.laptop} {
    flex-direction: ${props => props.$orientation};
    padding: ${props =>
      props.$orientation === 'column'
        ? props.theme.spacing(2)
        : props.theme.spacing(1, 2)};
  }
`;

export const StyledImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $alignColumnContent }) =>
    $alignColumnContent === 'center' ? 'center' : 'left'};
  overflow: hidden;
  object-position: left;
  object-fit: cover;
  height: ${props =>
    props.$imgHeight
      ? `${props.$imgHeight}px`
      : props.$mobileOrientation === 'column'
      ? '50px'
      : '85px'};
  width: ${props =>
    props.$imgWidth && props.$mobileOrientation === 'column'
      ? `${props.$imgWidth}px`
      : props.$imgWidth
      ? `${props.$imgWidth + 12}px`
      : props.$mobileOrientation === 'column'
      ? '65px'
      : '100px'};
  margin: ${props =>
    props.$mobileOrientation === 'column'
      ? props.theme.spacing(0, 0, 2, 0)
      : props.theme.spacing(0, 0, 0, 2)};
  img {
    max-height: 100%;
    max-width: ${props =>
      !!props.$imgWidth
        ? `${props.$imgWidth}px`
        : props.$mobileOrientation === 'column'
        ? '65px'
        : '82px'};
  }
  filter: ${props => props.$unavailable && 'grayscale(100%) brightness(93%)'};

  @media ${props => props.theme.laptop} {
    height: ${props =>
      props.$imgHeight
        ? `${props.$imgHeight}px`
        : props.$orientation === 'column'
        ? '50px'
        : '85px'};
    width: ${props =>
      props.$imgWidth && props.$orientation === 'column'
        ? `${props.$imgWidth}px`
        : props.$imgWidth
        ? `${props.$imgWidth + 12}px`
        : props.$orientation === 'column'
        ? '65px'
        : '100px'};
    margin: ${props =>
      props.$orientation === 'column'
        ? props.theme.spacing(0, 0, 2, 0)
        : props.theme.spacing(0, 0, 0, 2)};
    img {
      max-height: 100%;
      max-width: ${props =>
        !!props.$imgWidth
          ? `${props.$imgWidth}px`
          : props.$orientation === 'column'
          ? '65px'
          : '82px'};
    }
  }
`;

export const Info = styled.div`
  max-width: 200px;
  text-align: left;
  margin-top: ${props =>
    props.theme.spacing(props.$mobileOrientation === 'row' ? 2 : 0)};

  @media ${props => props.theme.laptop} {
    margin-top: ${props =>
      props.theme.spacing(props.$orientation === 'row' ? 2 : 0)};
  }
`;

export const CardHeader = styled.div`
  color: ${props => props.theme.colors.text.primary};
  display: flex;
  justify-content: ${({ $alignColumnContent }) =>
    $alignColumnContent === 'left' ? 'flex-start' : 'center'};
  gap: ${props => props.theme.spacing(4)};
`;

export const Name = styled.h5`
  ${props =>
    props.$typography
      ? props.theme.typography[props.$typography]
      : props.theme.typography.heading4}
  text-transform: capitalize;
`;

export const Description = styled.p`
  ${({ theme }) => theme.typography.subheading}
  color: ${props => props.theme.colors.text.secondary};
`;

export const MoreDetails = styled.button`
  ${({ theme }) => theme.typography.body}
  left: ${props => props.theme.spacing(4)};
  bottom: ${props => props.theme.spacing(8)};
  display: flex;
  border: none;
  visibility: ${props => (props.$unavailable ? 'hidden' : 'visible')};
  background-color: transparent;
  color: ${({ theme }) => theme.colors.text.link};
  text-align: left;
  z-index: 1;
  padding: ${props =>
    props.$mobileOrientation === 'row' ? props.theme.spacing(0, 0, 2) : '0'};

  svg {
    margin-left: ${props => props.theme.spacing(1)};
  }
  &:focus {
    border-radius: ${props => props.theme.spacing(2)};
    outline: 2px solid ${({ theme }) => theme.colors.neutral.darkest};
  }
  @media ${props => props.theme.laptop} {
    padding: ${props =>
      props.$orientation === 'row' ? props.theme.spacing(0, 0, 2) : '0'};
  }
`;

export const GetNotified = styled.button`
  ${({ theme }) => theme.typography.body}
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  border: none;
  background-color: transparent;
  color: ${({ theme, $isNotified }) =>
    $isNotified ? theme.colors.text.primary : theme.colors.text.link};
  cursor: ${({ $isNotified }) => ($isNotified ? 'default' : 'pointer')};
  text-align: left;
  position: relative;
  z-index: 1;
  padding: ${props =>
    props.$mobileOrientation === 'row'
      ? props.theme.spacing(0, 0, 2)
      : props.theme.spacing(1, 0, 0)};

  svg {
    margin-left: ${props => props.theme.spacing(1)};
  }
  &:focus {
    border-radius: ${props => props.theme.spacing(2)};
    outline: 2px solid ${({ theme }) => theme.colors.neutral.darkest};
  }
  @media ${props => props.theme.laptop} {
    padding: ${props =>
      props.$orientation === 'row' ? props.theme.spacing(0, 0, 2) : '0'};
  }
`;

export const BadgeWrapper = styled.div`
  border-radius: 20px;
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  p {
    ${({ theme }) => theme.fontFamily.condensed}
    padding: ${props => props.theme.spacing(4)};
    max-height: 20px;
  }

  ${props =>
    props.$outOfStock &&
    css`
      top: -5px;
      left: 65%;
      p {
        padding: ${props => props.theme.spacing(2, 4)};
        max-height: 27px;
      }
    `}
`;

export const GetNotifiedModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${({ theme }) => theme.spacing(4)};
  max-width: 400px;

  @media ${props => props.theme.laptop} {
    margin: ${({ theme }) => theme.spacing(4, 10)};
  }
`;

export const GetNotifiedModalImage = styled.div`
  width: max-content;
`;

export const GetNotifiedModalHeading = styled.h2`
  ${({ theme }) => theme.typography.heading3};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

export const GetNotifiedModalDescription = styled.p`
  ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

export const GetNotifiedModalCapture = styled.div`
  width: 100%;
`;

const slideUp = keyframes`
  0% { transform: translateY(100%); opacity: 0; }
  10% { transform: translateY(0); opacity: 1; }
   95% { transform: translateY(0); opacity: 1; }
   100% { transform: translateY(50%); opacity: 0; }
`;

export const SuccessPopUpBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: left;
  ${props => props.theme.typography.bodyBold};
  min-height: 52px;
  position: fixed;
  bottom: 128px;
  left: 0;
  right: 0;
  margin: auto;
  width: 100%;
  background-color: ${props => props.theme.colors.status.successPrimary};
  color: ${props => props.theme.colors.text.lightest};
  padding: ${({ theme }) => theme.spacing(4, 7)};
  border-radius: 16px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  animation: ${slideUp} 5s ease-in forwards;
  font-size: 1rem;
  z-index: 9999;
  max-width: 350px;

  svg {
    flex-shrink: 0;
    margin-right: 10px;
  }
`;

export const IconContainer = styled.div`
  position: absolute;
  top: ${({ $mobileOrientation, $offsetBadge }) =>
    $mobileOrientation === 'column' && $offsetBadge ? '28px' : '12px'};
  right: ${({ $mobileOrientation, $offsetBadge }) =>
    $mobileOrientation === 'column' && $offsetBadge ? '24px' : '12px'};
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  min-height: 28px;

  @media ${props => props.theme.laptop} {
    top: ${({ $orientation, $offsetBadge }) =>
      $orientation === 'column' && $offsetBadge ? '28px' : '12px'};
    right: ${({ $orientation, $offsetBadge }) =>
      $orientation === 'column' && $offsetBadge ? '24px' : '12px'};
  }
`;
